import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const MenuNodeRow = ({
  node,
  depth,
  isFirst,
  isLast,
  isExpanded,
  onToggleExpand,
  onAdd,
  onRename,
  onMoveUp,
  onMoveDown,
  onDelete,
  isSaving,
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(node.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleRenameSubmit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== node.name) {
      onRename(node.id, trimmed);
    } else {
      setEditValue(node.name);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRenameSubmit();
    if (e.key === 'Escape') { setEditValue(node.name); setEditing(false); }
  };

  const hasChildren = node.children?.length > 0;

  return (
    <div className="select-none">
      <div
        className="flex items-center gap-1 py-1 pr-2 rounded-md hover:bg-muted/50 group"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {/* Expand toggle */}
        <button
          className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0"
          onClick={() => hasChildren && onToggleExpand(node.id)}
          tabIndex={-1}
        >
          {hasChildren ? (
            <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={14} />
          ) : (
            <span className="w-3.5 h-px bg-border block" />
          )}
        </button>

        {/* Node name / inline edit */}
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            className="flex-1 text-sm bg-background border border-primary rounded px-1.5 py-0.5 outline-none"
          />
        ) : (
          <span
            className="flex-1 text-sm text-foreground truncate cursor-default"
            onDoubleClick={() => { setEditValue(node.name); setEditing(true); }}
            title="Doble clic para renombrar"
          >
            {node.name}
          </span>
        )}

        {/* Saving indicator */}
        {isSaving && (
          <Icon name="Loader2" size={14} className="text-muted-foreground animate-spin flex-shrink-0" />
        )}

        {/* Actions (visible on hover) */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={() => onMoveUp(node.id)}
            disabled={isFirst}
            className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30"
            title="Subir"
          >
            <Icon name="ChevronUp" size={13} />
          </button>
          <button
            onClick={() => onMoveDown(node.id)}
            disabled={isLast}
            className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30"
            title="Bajar"
          >
            <Icon name="ChevronDown" size={13} />
          </button>
          <button
            onClick={() => onAdd(node.id)}
            className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground"
            title="Añadir subcategoría"
          >
            <Icon name="Plus" size={13} />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-destructive"
            title="Eliminar"
          >
            <Icon name="Trash2" size={13} />
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child, idx) => (
            <MenuNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              isFirst={idx === 0}
              isLast={idx === node.children.length - 1}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
              onAdd={onAdd}
              onRename={onRename}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDelete={onDelete}
              isSaving={isSaving}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuNodeRow;
